import React from 'react';
import { Form, Input } from 'formik-antd';
import { Divider, Row, Col, Button } from 'antd';
import {
  FormikProps,
  Formik,
  prepareDataForValidation,
  FieldArray,
  FieldArrayRenderProps,
} from 'formik';
import * as Yup from 'yup';
import cx from 'classnames';

import ContactsSection, {
  contactsValidationSchema,
} from 'components/modals/sections/ContactsSection';
import FormItem from 'components/formik/FormItem';
import FormikEffect from 'components/formik/FormikEffect';
import { urlRegex } from 'utils/urlUtils';
import { countryUSAid } from 'constants/index';
import InternationalCompanyStateForm from './InternationalCompanyStateForm';
import CompanyStateForm from './CompanyStateForm';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

import styles from './GeneralInfo.module.less';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<GeneralInfoFormValues>) => void;
  initialValues?: GeneralInfoFormValues;
};

export interface PrimaryContactFormValues {
  id?: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone?: string;
}

export interface GeneralInfoFormValues {
  companyName: string;
  country?: number;
  internationalCityName?: string;
  internationalStateName?: string;
  state?: number;
  city: string;
  mainAddress: string;
  zip: string;
  companyWebsite: string;
  companyEmail: string;
  companyPhone: string;
  primaryContacts: Array<PrimaryContactFormValues>;
}

export const isGeneralInfoFormValidForDraft = (
  formik: FormikProps<GeneralInfoFormValues>,
) => {
  const dataForValidation = prepareDataForValidation(formik.values);

  return draftValidationSchema.isValidSync(dataForValidation);
};

const MAX_PRIMARY_CONTACTS_LENGTH = 50;

const isRemovePrimaryContactButtonVisible = (
  primaryContacts: Array<PrimaryContactFormValues>,
) => primaryContacts.length > 1;

const isAddPrimaryContactButtonVisible = (
  primaryContacts: Array<PrimaryContactFormValues>,
  index: number,
) =>
  primaryContacts.length < MAX_PRIMARY_CONTACTS_LENGTH &&
  index === primaryContacts.length - 1;

const initialValidationSchema = {
  companyName: Yup.string()
    .trim()
    .max(120, 'Company name should be 120 characters maximum'),
  country: Yup.number().nullable(),
  state: Yup.string().trim(),
  internationalStateName: Yup.string().when('country', {
    is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
    then: Yup.string()
      .trim()
      .max(120, 'State should be 120 characters maximum'),
  }),
  city: Yup.string()
    .trim()
    .when('country', {
      is: (country: number) => checkIsCountriesWithStatesDropdown(country),
      then: Yup.string()
        .trim()
        .max(120, 'City should be 120 characters maximum'),
    }),
  internationalCityName: Yup.string()
    .trim()
    .when('country', {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string()
        .trim()
        .max(120, 'City should be 120 characters maximum'),
    }),
  mainAddress: Yup.string().max(
    120,
    'Main address should be 120 characters maximum',
  ),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),
  companyEmail: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  companyPhone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
  companyWebsite: Yup.string()
    .matches(urlRegex, 'Company website format is invalid')
    .max(120, 'Company website should be 120 characters maximum'),
  primaryContacts: Yup.array().of(Yup.object(contactsValidationSchema)),
};

const draftValidationSchema = Yup.object({
  ...initialValidationSchema,
  companyName:
    initialValidationSchema.companyName.required('Field is required'),
});

const submitValidationSchema = Yup.object({
  ...initialValidationSchema,
  companyName:
    initialValidationSchema.companyName.required('Field is required'),
  country: initialValidationSchema.country.required('Field is required'),
  state: initialValidationSchema.state.when('country', {
    is: (country: number) => checkIsCountriesWithStatesDropdown(country),
    then: Yup.string().trim().required('Field is required'),
  }),
  internationalStateName: initialValidationSchema.internationalStateName.when(
    'country',
    {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().trim().required('Field is required'),
    },
  ),
  city: initialValidationSchema.city.when('country', {
    is: (country: number) => checkIsCountriesWithStatesDropdown(country),
    then: Yup.string().trim().required('Field is required'),
  }),
  internationalCityName: initialValidationSchema.internationalCityName.when(
    'country',
    {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().trim().required('Field is required'),
    },
  ),
  mainAddress:
    initialValidationSchema.mainAddress.required('Field is required'),
  companyWebsite:
    initialValidationSchema.companyWebsite.required('Field is required'),
  companyPhone:
    initialValidationSchema.companyPhone.required('Field is required'),
});

const PrimaryContactsFieldArray = React.memo(
  (props: FieldArrayRenderProps) => {
    const { primaryContacts } = (
      props.form as FormikProps<GeneralInfoFormValues>
    ).values;

    return (
      <>
        {primaryContacts.length > 0 &&
          primaryContacts.map(
            (primaryContact: PrimaryContactFormValues, index: number) => (
              <div key={index} className={styles.primaryContactSection}>
                <ContactsSection
                  index={index}
                  areFieldsRequired={false}
                  isPhoneRequired={false}
                  formNamePrefix={props.name}
                  actionButtons={
                    <>
                      {isAddPrimaryContactButtonVisible(
                        primaryContacts,
                        index,
                      ) && (
                        <Button
                          className={cx('borderless-btn', styles.btn)}
                          onClick={() =>
                            props.push({
                              firstName: '',
                              lastName: '',
                              title: '',
                              email: '',
                              phone: '',
                            })
                          }
                          icon={<span className="icon icon-plus-filled"></span>}
                        />
                      )}

                      {isRemovePrimaryContactButtonVisible(primaryContacts) && (
                        <Button
                          className={cx('borderless-btn', styles.btn)}
                          onClick={() => props.remove(index)}
                          icon={<span className="icon icon-closed-filled" />}
                        />
                      )}
                    </>
                  }
                />

                <Divider className="modal-divider after-form" />
              </div>
            ),
          )}
      </>
    );
  },
  (prevProps: FieldArrayRenderProps, nextProps: FieldArrayRenderProps) => {
    return (
      (prevProps.form.values as GeneralInfoFormValues).primaryContacts
        .length ===
      (nextProps.form.values as GeneralInfoFormValues).primaryContacts.length
    );
  },
);

export default React.memo((props: Props) => {
  const { formRef, onChange, initialValues: predefinedInitialValues } = props;
  const initialValues: GeneralInfoFormValues = {
    companyName: '',
    country: countryUSAid,
    internationalStateName: '',
    state: undefined,
    internationalCityName: '',
    city: '',
    mainAddress: '',
    zip: '',
    companyEmail: '',
    companyPhone: '',
    companyWebsite: '',
    primaryContacts: [
      {
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        phone: '',
      },
    ],
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">General information</div>
            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="companyName"
                  label="Company name *"
                  data-id="newClientCompanyNameField"
                >
                  <Input name="companyName" placeholder="Enter company name" autoComplete="off" />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="companyWebsite"
                  label="Company website *"
                  data-id="newClientCompanyWebsiteField"
                >
                  <Input
                    name="companyWebsite"
                    placeholder="Enter the website"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>
            </Row>
            {checkIsCountriesWithStatesDropdown(formik.values.country) ? (
              <CompanyStateForm formik={formik} />
            ) : (
              <InternationalCompanyStateForm formik={formik} />
            )}
            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="companyEmail"
                  label="Email"
                  data-id="newClientGeneralEmailField"
                >
                  <Input name="companyEmail" placeholder="Enter email" autoComplete="off" />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  data-id="newClientGeneraPhoneField"
                  label="Phone number *"
                  name="companyPhone"
                >
                  <Input name="companyPhone" placeholder="Enter phone number" autoComplete="off" />
                </FormItem>
              </Col>
            </Row>

            <Divider className="modal-divider after-form" />

            <div className="modal-title">Primary contact</div>

            <FieldArray
              name="primaryContacts"
              component={PrimaryContactsFieldArray as any}
            />
          </Form>
        </>
      )}
    </Formik>
  );
});
